import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { importCurriculum, previewCurriculumImport } from '@platform/client';
import {
  importPreviewSchema,
  importResultSchema,
  PLANNED_COLUMNS,
} from '@platform/schema/curriculum';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useTrackedMutation } from '../actions/ActionsProvider';
import { describeApiError } from '../api-error';
import { counted } from '../library/counted';

import type { ImportPreview, ImportResult } from '@platform/schema/curriculum';

/** How many changes the preview lists before it says how many more there are. */
const SHOWN = 50;

/** Pick a file, read what applying it would do, then apply it. Nothing is written before. */
export function ImportDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const queries = useQueryClient();
  const [file, setFile] = useState<{ name: string; csv: string } | null>(null);

  const preview = useTrackedMutation('Preview the curriculum import', {
    mutationFn: async (csv: string): Promise<ImportPreview> =>
      importPreviewSchema.parse(
        (await previewCurriculumImport({ body: { csv }, throwOnError: true }))
          .data,
      ),
  });
  const apply = useTrackedMutation('Import the curriculum', {
    mutationFn: async (csv: string): Promise<ImportResult> =>
      importResultSchema.parse(
        (await importCurriculum({ body: { csv }, throwOnError: true })).data,
      ),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: ['curriculum'] });
    },
  });

  const close = () => {
    setFile(null);
    preview.reset();
    apply.reset();
    onClose();
  };

  const read = async (picked: File | undefined) => {
    if (picked === undefined) return;
    const csv = await picked.text();
    setFile({ name: picked.name, csv });
    apply.reset();
    preview.mutate(csv);
  };

  const shown = preview.data;
  const headerBroken = shown?.issues.some((issue) => issue.line === null);

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="md">
      <DialogTitle>Import the curriculum CSV</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Button
            component="label"
            variant="outlined"
            sx={{ alignSelf: 'start' }}
          >
            {file === null ? 'Choose a CSV file' : 'Choose another file'}
            <input
              hidden
              type="file"
              accept=".csv,text/csv"
              aria-label="Curriculum CSV file"
              onChange={(event) => {
                void read(event.target.files?.[0]);
                event.target.value = '';
              }}
            />
          </Button>
          {file !== null && (
            <Typography variant="body2">{file.name}</Typography>
          )}
          {preview.isPending && <Typography>Reading the file…</Typography>}
          {preview.isError && (
            <Alert severity="error">{describeApiError(preview.error)}</Alert>
          )}
          {shown !== undefined && apply.data === undefined && (
            <PreviewBody preview={shown} />
          )}
          {apply.isError && (
            <Alert severity="error">{describeApiError(apply.error)}</Alert>
          )}
          {apply.data !== undefined && <ResultBody result={apply.data} />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>{apply.data ? 'Done' : 'Cancel'}</Button>
        {apply.data === undefined && (
          <Button
            variant="contained"
            disabled={
              file === null ||
              shown === undefined ||
              headerBroken === true ||
              apply.isPending
            }
            onClick={() => {
              if (file !== null) apply.mutate(file.csv);
            }}
          >
            Apply import
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function PreviewBody({ preview }: { preview: ImportPreview }) {
  const lineIssues = preview.issues.filter((issue) => issue.line !== null);
  const headerIssues = preview.issues.filter((issue) => issue.line === null);
  const newCourses = preview.courses.filter((course) => course.isNew);
  const newModules = preview.courses.flatMap((course) =>
    course.modules.filter((module) => module.isNew),
  );

  return (
    <Stack spacing={2}>
      <Typography>
        {counted(preview.rowCount, 'row')} in the file.{' '}
        {counted(preview.additions.length, 'new lesson')},{' '}
        {counted(
          new Set(preview.changes.map((change) => change.lessonId)).size,
          'lesson',
        )}{' '}
        with changes, {counted(newCourses.length, 'new course')},{' '}
        {counted(newModules.length, 'new module')}.
      </Typography>
      {headerIssues.length > 0 && (
        <Alert severity="error">
          The header has problems, so nothing can be imported:{' '}
          {headerIssues.map((issue) => issue.message).join(' ')}
        </Alert>
      )}
      {lineIssues.length > 0 && (
        <Alert severity="warning">
          {counted(lineIssues.length, 'problem')} — these lines will be skipped
          and the rest imported. Fix them in the file and import it again.
          <ul>
            {lineIssues.map((issue, index) => (
              <li key={index}>{issue.message}</li>
            ))}
          </ul>
        </Alert>
      )}
      {preview.missing.length > 0 && (
        <Typography variant="body2" color="text.secondary">
          {counted(preview.missing.length, 'planned lesson')} are not in this
          file. Importing never deletes them.
        </Typography>
      )}

      <Typography variant="subtitle2">Columns</Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>In the file</TableCell>
            <TableCell>Read as</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {preview.columns.map((column) => (
            <TableRow key={column.header}>
              <TableCell>{column.header}</TableCell>
              <TableCell>
                {column.field === null
                  ? 'Kept as an extra column'
                  : PLANNED_COLUMNS[column.field]}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Typography variant="subtitle2">Courses and modules</Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Course</TableCell>
            <TableCell>Modules</TableCell>
            <TableCell>Lessons</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {preview.courses.map((course) => (
            <TableRow key={course.name}>
              <TableCell>
                {course.name}
                {course.isNew ? ' (new)' : ''}
              </TableCell>
              <TableCell>
                {course.modules.length}
                {course.modules.some((module) => module.isNew)
                  ? ` (${String(course.modules.filter((module) => module.isNew).length)} new)`
                  : ''}
              </TableCell>
              <TableCell>
                {course.modules.reduce(
                  (sum, module) => sum + module.lessons,
                  0,
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {preview.changes.length > 0 && (
        <>
          <Typography variant="subtitle2">
            Changes to lessons already planned
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Lesson ID</TableCell>
                <TableCell>Column</TableCell>
                <TableCell>Now</TableCell>
                <TableCell>In the file</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {preview.changes.slice(0, SHOWN).map((change) => (
                <TableRow key={`${change.lessonId}-${change.column}`}>
                  <TableCell>{change.lessonId}</TableCell>
                  <TableCell>{change.column}</TableCell>
                  <TableCell>{change.before}</TableCell>
                  <TableCell>
                    {change.after}
                    {change.kept ? ' — kept as edited, not imported' : ''}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {preview.changes.length > SHOWN && (
            <Typography variant="body2" color="text.secondary">
              And {counted(preview.changes.length - SHOWN, 'more change')}.
            </Typography>
          )}
        </>
      )}
    </Stack>
  );
}

function ResultBody({ result }: { result: ImportResult }) {
  return (
    <Stack spacing={1}>
      <Alert severity="success">
        Imported. {counted(result.added, 'lesson')} added,{' '}
        {counted(result.changed, 'lesson')} changed,{' '}
        {counted(result.coursesAdded, 'course')} and{' '}
        {counted(result.modulesAdded, 'module')} added. The plan now has{' '}
        {counted(result.total, 'lesson')}.
        {result.kept > 0 &&
          ` ${counted(result.kept, 'change')} left out because an admin had edited the field.`}
      </Alert>
      {result.skipped.length > 0 && (
        <Alert severity="warning">
          {counted(result.skipped.length, 'line')} skipped:
          <ul>
            {result.skipped.map((issue, index) => (
              <li key={index}>{issue.message}</li>
            ))}
          </ul>
        </Alert>
      )}
    </Stack>
  );
}
